import React, { useState } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, startOfDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

const WizardDatePickerModal = ({ isOpen, onClose, onConfirm, initialRange }) => {
  const [tempRange, setTempRange] = useState(initialRange || { start: null, end: null });
  const [calendarMonth, setCalendarMonth] = useState(initialRange?.start ? new Date(initialRange.start) : new Date());
  const [selectingDateType, setSelectingDateType] = useState('start');

  if (!isOpen) return null;

  const handleDateSelect = (date) => {
    if (selectingDateType === 'start') {
      setTempRange({ start: startOfDay(date), end: null });
      setSelectingDateType('end');
    } else {
      if (date < tempRange.start) {
        setTempRange({ start: startOfDay(date), end: endOfDay(tempRange.start) });
      } else {
        setTempRange({ ...tempRange, end: endOfDay(date) });
      }
      setSelectingDateType('start');
    }
  };

  const handleConfirm = () => {
    if (tempRange.start && tempRange.end) {
      onConfirm(tempRange);
    }
  };

  const monthStart = startOfMonth(calendarMonth);
  const monthEnd = endOfMonth(calendarMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  // Week starts on Saturday (S D L M M J V). getDay() returns 0 for Sunday, 6 for Saturday.
  const startDayOfWeek = getDay(monthStart);
  const adjustedStartDay = (startDayOfWeek + 1) % 7;
  const weekDays = ['S', 'D', 'L', 'M', 'M', 'J', 'V'];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl p-6 w-full max-w-[420px] animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6 px-2">
          <button
            onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
            className="p-2 hover:bg-purple-50 text-[#5B21B6] rounded-xl transition-colors"
          >
            <ChevronLeft size={20} strokeWidth={2.5} />
          </button>
          <h4 className="text-xl font-bold text-[#5B21B6]">
            {format(calendarMonth, 'MMMM yyyy', { locale: fr })}
          </h4>
          <button
            onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
            className="p-2 hover:bg-purple-50 text-[#5B21B6] rounded-xl transition-colors"
          >
            <ChevronRight size={20} strokeWidth={2.5} />
          </button>
        </div>

        {/* Days of week */}
        <div className="grid grid-cols-7 gap-2 mb-4">
          {weekDays.map((day, idx) => (
            <div key={idx} className="text-center text-sm font-bold text-gray-400">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-y-2 gap-x-0">
          {Array.from({ length: adjustedStartDay }).map((_, idx) => (
            <div key={`empty-${idx}`} />
          ))}

          {daysInMonth.map((day) => {
            const dayStart = startOfDay(day);
            const isStart = tempRange?.start && dayStart.getTime() === startOfDay(tempRange.start).getTime();
            const isEnd = tempRange?.end && dayStart.getTime() === startOfDay(tempRange.end).getTime();
            const isInRange = tempRange?.start && tempRange?.end && dayStart > startOfDay(tempRange.start) && dayStart < startOfDay(tempRange.end);

            let roundedClasses = 'rounded-xl';
            if (isStart && tempRange?.end && !isEnd) roundedClasses = 'rounded-l-xl rounded-r-none';
            if (isEnd && tempRange?.start && !isStart) roundedClasses = 'rounded-r-xl rounded-l-none';
            if (isInRange) roundedClasses = 'rounded-none';
            if (isStart && isEnd) roundedClasses = 'rounded-xl';

            return (
              <div key={day.toISOString()} className={`p-0.5 ${isInRange ? 'bg-purple-50/50' : isStart && tempRange?.end ? 'bg-gradient-to-r from-transparent to-purple-50/50' : isEnd && tempRange?.start ? 'bg-gradient-to-l from-transparent to-purple-50/50' : ''}`}>
                <button
                  onClick={() => handleDateSelect(day)}
                  className={`
                    w-full h-11 flex items-center justify-center font-bold text-sm transition-colors
                    ${roundedClasses}
                    ${isStart || isEnd
                      ? 'bg-[#5B21B6] text-white shadow-sm'
                      : isInRange
                        ? 'bg-purple-50/50 text-[#5B21B6]'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-[#5B21B6]'
                    }
                  `}
                >
                  {format(day, 'd')}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-6 text-center text-[#5B21B6] text-sm font-semibold">
          {selectingDateType === 'start' ? 'Sélectionnez la date de début' : 'Sélectionnez la date de fin'}
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center justify-center gap-3 mt-4 shadow-sm">
          <Calendar className="text-[#5B21B6]" size={20} strokeWidth={2.5} />
          <span className="font-bold text-gray-900 text-sm">
            {tempRange?.start ? format(tempRange.start, 'dd MMM', { locale: fr }) : '__ ___'} 
            <span className="text-gray-400 mx-2">→</span> 
            {tempRange?.end ? format(tempRange.end, 'dd MMM yyyy', { locale: fr }) : '__ ___ ____'}
          </span>
        </div>

        <div className="flex items-center justify-between pt-6 mt-2">
          <button
            onClick={onClose}
            className="text-[#5B21B6] hover:bg-purple-50 px-6 py-3 rounded-xl font-bold transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={!tempRange?.start || !tempRange?.end}
            className="bg-[#5B21B6] hover:bg-[#4C1D95] text-white px-8 py-3 rounded-xl font-bold transition-colors disabled:opacity-50 shadow-sm"
          >
            Confirmer
          </button>
        </div>
      </div>
    </div>
  );
};

export default WizardDatePickerModal;
